/**
 * @kgc/rental-pricing — Tier-based duration discount (PURE)
 * ADR-108 P2: calendar-day tier discount application.
 *
 * Logic (Story 78-1, ADR-048):
 *  - Tiers are sorted by minDays ascending.
 *  - The HIGHEST tier whose minDays ≤ calendarDays wins ("szintlépéses").
 *  - No stacking. Only one tier applies.
 *
 * The tier list is the caller's responsibility:
 *  - Backend: loads from DB (RentalDiscountTier per tenant).
 *  - Frontend: uses hardcoded defaults for live preview.
 */

export interface DiscountTier {
  /** Minimum calendar days required for this tier */
  minDays: number;
  /** Discount percentage (0-100) */
  discountPercent: number;
}

export interface TierDiscountResult {
  discountPercent: number;
  discountAmount: number;
  finalAmount: number;
  appliedTier: DiscountTier | null;
}

/**
 * Default fallback tiers — used by frontend preview when no per-tenant
 * config is available. Backend MUST always pass explicit tiers from DB.
 *
 * Forrás: ADR-037-amendment-1 + ADR-048 (kliens-ratify, Javo value-pin 2026-06-15)
 * Logika: 7-13 nap → 10%, 14-20 nap → 15%, 21-29 nap → 25%, 30+ nap → 30%
 */
export const DEFAULT_RENTAL_DISCOUNT_TIERS: readonly DiscountTier[] = Object.freeze([
  { minDays: 1, discountPercent: 0 },
  { minDays: 7, discountPercent: 10 },
  { minDays: 14, discountPercent: 15 },
  { minDays: 21, discountPercent: 25 },
  { minDays: 30, discountPercent: 30 },
]);

/**
 * Apply the highest eligible tier discount to a subtotal.
 *
 * @param subtotal       Net subtotal in HUF, pre-discount
 * @param calendarDays   Rental duration in calendar days
 * @param tiers          Sorted (or unsorted — we sort) list of tiers
 */
export function applyTierDiscount(
  subtotal: number,
  calendarDays: number,
  tiers: readonly DiscountTier[] = DEFAULT_RENTAL_DISCOUNT_TIERS
): TierDiscountResult {
  if (subtotal <= 0 || tiers.length === 0) {
    return {
      discountPercent: 0,
      discountAmount: 0,
      finalAmount: Math.max(0, subtotal),
      appliedTier: null,
    };
  }

  // Sort ascending by minDays, find highest eligible.
  const sorted = [...tiers].sort((a, b) => a.minDays - b.minDays);
  let applied: DiscountTier | null = null;

  for (const tier of sorted) {
    if (calendarDays >= tier.minDays) {
      applied = tier;
    } else {
      break;
    }
  }

  if (!applied || applied.discountPercent === 0) {
    return {
      discountPercent: applied?.discountPercent ?? 0,
      discountAmount: 0,
      finalAmount: subtotal,
      appliedTier: applied,
    };
  }

  const discountAmount = Math.round((subtotal * applied.discountPercent) / 100);
  return {
    discountPercent: applied.discountPercent,
    discountAmount,
    finalAmount: subtotal - discountAmount,
    appliedTier: applied,
  };
}
